// MapObject_ForTerrain_Augments.ts — reshapes PACKAGED terrain-objects into the material/scenery
// object shapes the export-dialog fixtures feed to `collectMaterialRegions` / `collectSceneryRegions`.
//
// `PackageUpMapObjForTerrain`'s whitelist forwards only the HEIGHT-GEN modifiers
// (terrainDefinition / terrainRegion / terrainSpotHeight) and DROPS the map-wide
// `Mod_TerrainMaterials` (userRecipes / baseRecipeID / blendDefaults) and `Mod_TerrainSceneries`
// (userKinds / budget) modifiers. But the material/scenery pipelines ARE affected by those, so a
// bare packaged object would silently lose them and the fixture couldn't reproduce the live map.
//
// These functions take the PACKAGED terrain-objects (already run through
// `ResolveMapObjectsForTerrain`) plus the collector's RAW relevant MapObjects
// (`TerrainExportResult.objects` from `Collect[Material|Scenery]RelevantObjects`), find each
// packaged object's original modifier data by id, and REPLACE the object's `modifiers` array with
// ONLY the enabled `terrainMaterials` / `terrainSceneries` entries — the height-gen modifier keys
// (terrainDefinition / terrainRegion / terrainSpotHeight) are omitted entirely (the material/scenery
// pipelines don't read them). The return shape is auto-inferred from the spread of the input
// object's data + the rebuilt `modifiers` array (mirroring `PackageUpMapObjForTerrain`), so the
// type is narrowed to the terrain-key-free modifier shape automatically.
//
// DB-free by design (like `MapObject_ForTerrain.ts`): it imports no mobx-graphlink / web-vcore /
// @apollo/client, so the Node test lane can import it without dragging in the browser-runtime tree.
// The modifier payloads are forwarded VERBATIM as `unknown` (the anti-drift rule — a new field on
// `Mod_TerrainMaterials` / `Mod_TerrainSceneries` needs no edit here); the raw MapObject is typed
// structurally for the same reason.

import {MapObject_ForTerrain} from "./MapObject_ForTerrain.js";

// The subset of `ObjModifier` this module reads (DB-free structural mirror — cannot import the
// class without dragging the browser-runtime tree into the Node lane). `enabled` is always present
// on a real modifier; the payload keys are optional because a single modifier carries at most one.
type ModifierLoose = {
	enabled: boolean;
	terrainMaterials?: unknown|n;
	terrainSceneries?: unknown|n;
};

// The subset of `MapObject` this module reads (DB-free structural mirror of the collector's output).
type MapObjectLoose = {
	id: string;
	modifiers?: Array<ModifierLoose>|n;
};

/** A modifier carrying ONLY the `enabled` flag + the material payload — the terrain height-gen
 *  keys are dropped. Auto-inferred by the `modifiers` rebuild below; named for clarity. */
type MaterialOnlyModifier = {enabled: boolean; terrainMaterials?: unknown};
/** A modifier carrying ONLY the `enabled` flag + the scenery payload. */
type SceneryOnlyModifier = {enabled: boolean; terrainSceneries?: unknown};

/** Reshape packaged terrain-objects into material-objects: drop height-gen modifiers, re-attach
 *  the map-wide `terrainMaterials` data (found via `relevantMapObjects`). */
export function AugmentTerrainObjectsWithMaterialData(
	terrainObjects: MapObject_ForTerrain[],
	relevantMapObjects: MapObjectLoose[],
): (Omit<MapObject_ForTerrain, "modifiers"> & {modifiers: MaterialOnlyModifier[]})[] {
	const originalsByID = new Map(relevantMapObjects.map(o=>[o.id, o] as const));
	return terrainObjects.map(obj=>{
		const original = originalsByID.get(obj.id);
		const materialMods = (original?.modifiers ?? []).filter(m=>m.enabled && m.terrainMaterials != null);
		return {
			...obj,
			modifiers: materialMods.map(m=>({
				enabled: m.enabled,
				...(m.terrainMaterials != null && {terrainMaterials: m.terrainMaterials}),
			})),
		};
	});
}

/** Reshape packaged terrain-objects into scenery-objects: drop height-gen modifiers, re-attach
 *  the map-wide `terrainSceneries` data (found via `relevantMapObjects`). */
export function AugmentTerrainObjectsWithSceneryData(
	terrainObjects: MapObject_ForTerrain[],
	relevantMapObjects: MapObjectLoose[],
): (Omit<MapObject_ForTerrain, "modifiers"> & {modifiers: SceneryOnlyModifier[]})[] {
	const originalsByID = new Map(relevantMapObjects.map(o=>[o.id, o] as const));
	return terrainObjects.map(obj=>{
		const original = originalsByID.get(obj.id);
		const sceneryMods = (original?.modifiers ?? []).filter(m=>m.enabled && m.terrainSceneries != null);
		return {
			...obj,
			modifiers: sceneryMods.map(m=>({
				enabled: m.enabled,
				...(m.terrainSceneries != null && {terrainSceneries: m.terrainSceneries}),
			})),
		};
	});
}
